import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Handles the return leg of the Google OAuth roundtrip.
 * The browser lands here at /auth/callback with `#session_id=xxxx` in the URL fragment.
 * We POST it to our backend, receive our normal JWT, store it via AuthContext,
 * then redirect to /dashboard. If anything fails, back to /login with a toast.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const { setSession } = useAuth();
  // useRef guards against React 18 StrictMode double-invocation
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = loc.hash || window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      toast.error("Google sign-in was cancelled");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        // Native app leg: the Expo app opens this page with ?native=1 and only needs
        // the session_id handed back through the custom scheme so it can exchange it.
        const isNative = new URLSearchParams(window.location.search).get("native") === "1";
        if (isNative) {
          window.location.replace(`splitthe://auth-callback#session_id=${encodeURIComponent(sessionId)}`);
          return;
        }
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setSession(data.token, data.user);
        // Clean the hash off the URL so a refresh doesn't re-trigger
        window.history.replaceState({}, "", window.location.pathname);
        toast.success(`Welcome, ${data.user.name.split(" ")[0]} ✨`);
        nav("/dashboard", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [loc.hash, nav, setSession]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="h-8 w-8 mx-auto rounded-full border-2 border-[color:var(--brand)] border-t-transparent animate-spin" />
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mt-4">Finishing sign-in…</p>
      </div>
    </div>
  );
}
